<template>
  <div class="dev-auth">
    <div class="dev-auth__panel">
      <h1>Dev auth token</h1>
      <p class="dev-auth__lead">
        Paste a backend2 JWT. It is stored in localStorage and used as
        <code>Authorization: Bearer …</code>, overriding
        <code>VUE_APP_DEV_TOKEN</code>.
      </p>

      <label class="dev-auth__label" for="dev-token">Bearer token</label>
      <textarea
        id="dev-token"
        v-model="draft"
        class="dev-auth__input"
        rows="6"
        spellcheck="false"
        placeholder="eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9…"
      />

      <div class="dev-auth__actions">
        <button type="button" class="dev-auth__btn" @click="save">
          Save token
        </button>
        <button
          type="button"
          class="dev-auth__btn dev-auth__btn--ghost"
          @click="clear"
        >
          Clear
        </button>
        <button
          type="button"
          class="dev-auth__btn dev-auth__btn--ghost"
          @click="openWallboard"
        >
          Open wallboard
        </button>
      </div>

      <p v-if="message" class="dev-auth__msg">{{ message }}</p>
      <p class="dev-auth__status">
        Active source:
        <strong>{{ sourceLabel }}</strong>
        <span v-if="preview"> — {{ preview }}</span>
      </p>
    </div>
  </div>
</template>

<script>
import { computed, ref } from "vue";
import { useStore } from "vuex";
import {
  clearStoredDevToken,
  getDevToken,
  getStoredDevToken,
  setStoredDevToken,
} from "../shared/devToken";

export default {
  name: "DevAuth",
  setup() {
    const store = useStore();
    const draft = ref(getStoredDevToken() || process.env.VUE_APP_DEV_TOKEN || "");
    const message = ref("");
    const tick = ref(0);

    const sourceLabel = computed(() => {
      tick.value;
      if (getStoredDevToken()) return "localStorage (UI)";
      if (process.env.VUE_APP_DEV_TOKEN) return "VUE_APP_DEV_TOKEN";
      if (store.getters.getAccessToken) return "store / Keycloak";
      return "none";
    });

    const preview = computed(() => {
      tick.value;
      const token =
        getDevToken() || store.getters.getAccessToken || "";
      if (!token) return "";
      if (token.length <= 24) return token;
      return `${token.slice(0, 12)}…${token.slice(-8)}`;
    });

    function save() {
      const token = setStoredDevToken(draft.value);
      if (!token) {
        message.value = "Enter a token first.";
        tick.value += 1;
        return;
      }
      store.dispatch("setAccessToken", token);
      message.value = "Saved. Requests will send Authorization: Bearer.";
      tick.value += 1;
    }

    function clear() {
      clearStoredDevToken();
      draft.value = "";
      store.dispatch("setAccessToken", null);
      message.value = "Cleared. Reloading will fall back to env / Keycloak.";
      tick.value += 1;
    }

    function openWallboard() {
      // Full reload so App.vue runs the wallboard data load with the new token.
      window.location.href = "/";
    }

    return {
      draft,
      message,
      sourceLabel,
      preview,
      save,
      clear,
      openWallboard,
    };
  },
};
</script>

<style scoped>
.dev-auth {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  background:
    radial-gradient(ellipse at 20% 0%, #1e3a5f 0%, transparent 50%),
    radial-gradient(ellipse at 80% 100%, #0f766e 0%, transparent 45%),
    #0b1220;
  color: #e8eef7;
  font-family: "IBM Plex Sans", "Segoe UI", sans-serif;
}

.dev-auth__panel {
  width: min(560px, 100%);
}

.dev-auth h1 {
  margin: 0 0 0.5rem;
  font-family: "IBM Plex Serif", Georgia, serif;
  font-size: 1.75rem;
  font-weight: 600;
}

.dev-auth__lead {
  margin: 0 0 1.25rem;
  line-height: 1.45;
  color: #a8b8cc;
  font-size: 0.95rem;
}

.dev-auth__lead code {
  font-size: 0.85em;
  color: #7dd3c0;
}

.dev-auth__label {
  display: block;
  margin-bottom: 0.35rem;
  font-size: 0.8rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #8fa3b8;
}

.dev-auth__input {
  width: 100%;
  box-sizing: border-box;
  padding: 0.75rem 0.85rem;
  border: 1px solid #2a3f57;
  border-radius: 6px;
  background: #111a2b;
  color: #e8eef7;
  font-family: "IBM Plex Mono", ui-monospace, monospace;
  font-size: 0.8rem;
  line-height: 1.4;
  resize: vertical;
}

.dev-auth__input:focus {
  outline: none;
  border-color: #2dd4bf;
}

.dev-auth__actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  margin-top: 1rem;
}

.dev-auth__actions > * {
  margin-right: 0.65rem;
  margin-bottom: 0.65rem;
}

.dev-auth__btn {
  border: none;
  border-radius: 6px;
  padding: 0.55rem 1rem;
  background: #14b8a6;
  color: #042f2e;
  font-weight: 600;
  cursor: pointer;
}

.dev-auth__btn:hover {
  background: #2dd4bf;
}

.dev-auth__btn--ghost {
  background: transparent;
  color: #a8b8cc;
  border: 1px solid #2a3f57;
}

.dev-auth__btn--ghost:hover {
  border-color: #8fa3b8;
  color: #e8eef7;
}

.dev-auth__msg {
  margin: 1rem 0 0;
  color: #86efac;
  font-size: 0.9rem;
}

.dev-auth__status {
  margin: 0.75rem 0 0;
  color: #8fa3b8;
  font-size: 0.85rem;
  word-break: break-all;
}

.dev-auth__status strong {
  color: #e8eef7;
  font-weight: 600;
}
</style>
